import { ChangeDetectionStrategy, Component, HostListener, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, startWith, takeUntil, tap } from 'rxjs/operators';

import { GenerateEmbedCode, IsDirectEmbed } from '@mediahaven/shared';
import { ApiFragment, BaseContainer, DialogConfig, DialogRef, MediaType, MH2MediaType } from '@stylehaven/components';
import { ClipboardService } from 'ngx-clipboard';
import { IntellectualRecordsService } from '../../../services/intellectual-records/intellectual-records.service';

@Component({
    standalone: false,
    selector: 'mhv-embed-dialog',
    templateUrl: './embed-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmbedDialogComponent extends BaseContainer implements OnInit {
    public createEmbedCodeFormGroup: UntypedFormGroup;
    private embeddableChildren$: Observable<number>;
    public buttonDisabled$: Observable<boolean>;
    public disabledMessageKey$: BehaviorSubject<string> = new BehaviorSubject('media.embed-dialog.disabled.progress');

    public embedCode$: Observable<string>;
    public infoMessage$: Observable<string | null>;

    private readonly intellectualRecordsService: IntellectualRecordsService = inject(IntellectualRecordsService);
    private readonly clipboard: ClipboardService = inject(ClipboardService);

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        public config: DialogConfig,
        public dialog: DialogRef<boolean>
    ) {
        super();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(_event: KeyboardEvent) {
        this.embedCode$.pipe(takeUntil(this.destroy$)).subscribe(embedCode => {
            this.clipboard.copyFromContent(embedCode, document.querySelector('dialog') as HTMLElement);
            this.copySuccess();
        });
    }

    ngOnInit(): void {
        this.initForm();

        this.initEmbeddableChildrenStream();
        this.initDisableButtonStream();
        this.initNotification();

        this.initEmbeddedCodeGeneration();
    }

    private initForm(): void {
        this.createEmbedCodeFormGroup = this.formBuilder.group({
            autoplay: [],
            width: [],
            height: []
        });
    }

    private initEmbeddableChildrenStream(): void {
        this.embeddableChildren$ = this.intellectualRecordsService
            .getNrOfEmbeddableChildren(this.sourceRecord.getRecordId())
            .pipe(shareReplay(1));
    }

    private initDisableButtonStream(): void {
        if (this.sourceRecord.isIntellectualObject()) {
            this.buttonDisabled$ = this.embeddableChildren$.pipe(
                map(nrOfChildren => nrOfChildren === 0),
                tap(disabled =>
                    disabled
                        ? this.disabledMessageKey$.next('media.embed-dialog.disabled.no-children')
                        : this.disabledMessageKey$.next('')
                ),
                startWith(true)
            );
        } else {
            this.disabledMessageKey$.next('');
            this.buttonDisabled$ = of(false);
        }
    }

    // #region Embed codes
    private initEmbeddedCodeGeneration(): void {
        this.embedCode$ = this.createEmbedCodeFormGroup.valueChanges.pipe(
            map(_values => this.generateEmbeddedCode()),
            startWith(this.generateEmbeddedCode()),
            takeUntil(this.destroy$)
        );
    }

    private generateEmbeddedCode(): string {
        const width: number = this.widthControl.value;
        const height: number = this.heightControl.value;
        const autoplay: boolean = this.autoplayControl.value;

        return GenerateEmbedCode(this.embedBaseUrl, this.sourceRecord, width, height, autoplay);
    }
    // #endregion

    // #region Actions
    copySuccess(_event?: ClipboardEvent): void {
        this.dialog.close(true);
    }

    copyFailure(event: ClipboardEvent): void {
        console.error(`Copying to clipboard failed: ${event}`);
        this.dialog.close(false);
    }

    cancel(): void {
        this.dialog.close();
    }
    // #endregion

    // #region Notification
    private initNotification(): void {
        this.infoMessage$ = this.sourceRecord.isIntellectualObject() ? this.getNotification() : of(null);
    }

    private getNotification(): Observable<string | null> {
        return this.embeddableChildren$.pipe(
            map(embeddableNr =>
                this.translateService.translate('media.embed-dialog.embed-content', { total: embeddableNr })
            )
        );
    }
    // #endregion

    // #region Getters
    get sourceRecord(): ApiFragment {
        return this.config.data.record;
    }

    get embedBaseUrl(): string {
        return this.config.data.embedUrl;
    }

    get subtitle(): string {
        return this.config.data.subtitle;
    }

    get showDimensionControls(): boolean {
        const excludedTypes: MH2MediaType[] = [MediaType.audio, MediaType.audiofragment];
        return !excludedTypes.includes(this.sourceRecord.getType());
    }

    get showAutoplayControl(): boolean {
        return !IsDirectEmbed(this.sourceRecord);
    }

    get autoplayControl(): UntypedFormControl {
        return this.createEmbedCodeFormGroup.get('autoplay') as UntypedFormControl;
    }

    get widthControl(): UntypedFormControl {
        return this.createEmbedCodeFormGroup.get('width') as UntypedFormControl;
    }

    get heightControl(): UntypedFormControl {
        return this.createEmbedCodeFormGroup.get('height') as UntypedFormControl;
    }
    // #endregion
}
